import { Capacitor } from "@capacitor/core";
import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";

import type { Metric } from "../../lib/habits/heatmap";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

type HabitsHeaderTogglesProps = {
  metric: Metric;
  onMetricToggle: () => void;
};

export function HeaderTextToggle({
  label,
  onClick,
  ariaLabel,
  animateKey,
  toggleClassName = "habit-header-toggle",
  labelClassName = "col-start-1 row-start-1 truncate",
  stableWidthLabels,
}: {
  label: string;
  onClick: () => void;
  ariaLabel: string;
  animateKey: string;
  toggleClassName?: string;
  labelClassName?: string;
  stableWidthLabels?: readonly string[];
}) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <button
      type="button"
      onClick={onClick}
      className={toggleClassName}
      aria-label={ariaLabel}
    >
      <span className="relative grid overflow-hidden">
        {stableWidthLabels?.map((stableLabel) => (
          <span
            key={stableLabel}
            className="invisible col-start-1 row-start-1 whitespace-nowrap"
            aria-hidden
          >
            {stableLabel}
          </span>
        ))}
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={animateKey}
            className={labelClassName}
            initial={prefersReducedMotion ? false : { y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={prefersReducedMotion ? undefined : { y: "-100%", opacity: 0 }}
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : { type: "spring", stiffness: 520, damping: 34, mass: 0.7 }
            }
          >
            {label}
          </motion.span>
        </AnimatePresence>
      </span>
    </button>
  );
}

export function HabitsHeaderToggles({ metric, onMetricToggle }: HabitsHeaderTogglesProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const isAndroid = Capacitor.getPlatform() === "android";
  const metricLabels: Record<Metric, string> = {
    time: isAndroid ? t.today.metricScreenTimeAndroid : t.today.metricScreenTime,
    opens: t.today.metricActivations,
  };

  return (
    <HeaderTextToggle
      label={metricLabels[metric]}
      onClick={onMetricToggle}
      ariaLabel={formatTemplate(t.habits.metricToggleAria, { metric: metricLabels[metric] })}
      animateKey={metric}
    />
  );
}
